import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiCreateAccountRequest,
  ApiAccountCryptoAddressCreateRequest,
  ApiAccountUpdateNameRequest,
  ApiClient,
} from '../openapi';
import { useApiClient } from '../contexts/ApiContext';
import { downloadUrl } from '@razz-team/utils';
import { mapResponse } from './helpers';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { useMemo } from 'react';
import { uniqBy } from 'lodash-es';
import { useBusinessSettingsQuery } from './businesses';

export function useAccountsQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      client
        .GET('/accounts', { signal })
        .then(mapResponse)
        .then(({ accounts }) => accounts),
    retry: true,
  });
}

export function useCardLinkableCurrenciesQuery() {
  const settingsQuery = useBusinessSettingsQuery();

  const data = useMemo(() => {
    if (!settingsQuery.data) {
      return undefined;
    }

    const conditions = [
      ...(settingsQuery.data?.products?.fiatAccountProduct?.conditions ?? []),
      ...(settingsQuery.data?.products?.cryptoAccountProduct?.conditions ?? []),
    ];

    return conditions.flatMap((condition) => (condition.isCardLinkable ? condition.currency : []));
  }, [settingsQuery.data]);

  return {
    isLoading: settingsQuery.isLoading,
    data,
  };
}

export function useAccountsWithAccountQuery(accountId: string | null | undefined) {
  const accountsQuery = useAccountsQuery();
  const accountQuery = useAccountQuery({ accountId });

  const data = useMemo(() => {
    if (accountsQuery.data && accountQuery.data) {
      return uniqBy([...accountsQuery.data, accountQuery.data], 'id');
    }

    return accountsQuery.data ?? [];
  }, [accountsQuery.data, accountQuery.data]);

  const isPending = accountQuery.isEnabled
    ? accountsQuery.isPending || accountQuery.isPending
    : accountsQuery.isPending;
  const error = accountQuery.isEnabled
    ? (accountsQuery.error ?? accountQuery.error)
    : accountsQuery.error;
  return {
    data,
    isPending,
    error,
    isEnabled: accountsQuery.isEnabled && accountQuery.isEnabled,
  };
}

export function useAccountsSummary() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['accounts', 'summary'],
    queryFn: ({ signal }) => client.GET('/accounts/summary', { signal }).then(mapResponse),
  });
}

export function useAccountQuery({ accountId }: { accountId: string | null | undefined }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['accounts', accountId],
    queryFn: () =>
      client.GET('/accounts/{id}', { params: { path: { id: accountId! } } }).then(mapResponse),
    enabled: Boolean(accountId),
  });
}

export function createAccountAddressesQueryParams({
  client,
  accountId,
}: {
  client: ApiClient;
  accountId: string;
}) {
  return {
    queryKey: ['accounts', accountId, 'addresses'],
    queryFn: () =>
      client
        .GET('/accounts/{id}/addresses', { params: { path: { id: accountId } } })
        .then((res) => ({
          addresses: res.data?.addresses ?? [],
        })),
  };
}

export function useAccountAddressesQuery({ id: accountId }: { id: string }) {
  const client = useApiClient();
  return useQuery(createAccountAddressesQueryParams({ client, accountId }));
}

export function useAccountCreateCryptoAddressMutation({
  accountId,
  idempotencyKey,
}: {
  accountId: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accounts', accountId, 'addresses', 'crypto'],
    mutationFn: (body: ApiAccountCryptoAddressCreateRequest) =>
      api
        .POST('/accounts/{id}/addresses/crypto', {
          body,
          params: { path: { id: accountId } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['accounts', accountId, 'addresses'],
      }),
  });
}

export function useAccountProductFiatQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['accounts-product', 'fiat'],
    queryFn: ({ signal }) => client.GET('/accounts/products/fiat', { signal }).then(mapResponse),
  });
}

export function useAccountProductCryptoQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['accounts-product', 'crypto'],
    queryFn: ({ signal }) => client.GET('/accounts/products/crypto', { signal }).then(mapResponse),
  });
}

export function useAccountCreateMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accounts'],
    mutationFn: (body: ApiCreateAccountRequest) =>
      api
        .POST('/accounts', { body, headers: { 'Idempotency-Key': idempotencyKey } })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['accounts'],
      }),
  });
}

export function useAccountTerminateMutation({
  accountId,
  idempotencyKey,
}: {
  accountId: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accounts', accountId],
    mutationFn: () =>
      api
        .POST('/accounts/{id}/terminate', {
          params: { path: { id: accountId } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['accounts'],
      }),
  });
}

export function useAccountUpdateNameMutation({ accountId }: { accountId: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accounts', accountId, 'name'],
    mutationFn: (body: ApiAccountUpdateNameRequest) =>
      api
        .PUT('/accounts/{id}/name', {
          body,
          params: { path: { id: accountId } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['accounts'],
      }),
  });
}

export type StatementRequest =
  | { type: 'csv'; since?: string; until?: string }
  | { type: 'balance' }
  | { type: 'avg_balance'; since?: string; until?: string }
  | { type: 'transactions'; since?: string; until?: string }
  | { type: 'balance_and_transactions'; since?: string };

export function useAccountsStatementMutation({ accountId }: { accountId: string | undefined }) {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['accounts', accountId, 'statements'],
    mutationFn: ({ type, ...query }: StatementRequest) => {
      return api
        .POST(`/accounts/{id}/statement/${type}`, {
          params: { query, path: { id: accountId! } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(({ data }) => {
          if (data?.link) {
            downloadUrl(data.link);
          }
        });
    },
  });
}
